import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Theme, useMediaQuery } from "@mui/material";
import { AddToWatchlistModal } from "@features/watchlists/components/AddToWatchlistModal";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import truncate from "lodash/truncate";
import {
  CACHE_KEYS,
  InvestmentTypeEnum,
  useGetQuotesV2,
  useListAndSearchAllWatchlists,
} from "@rentablo/api/apiv1";
import { useGetSecurity } from "@rentablo/api/quotes";
import {
  QuoteChartContainer,
  useQuoteDateRanges,
} from "@features/securities/components/QuoteChartContainer";
import { ChartControlsProvider } from "@components/Chart/ChartControls";
import { CardPageLayout, Tab, Tabs } from "@rentablo/components";
import { WatchlistV2Required } from "@features/watchlists/types";
import { useMandatorVisibility } from "@helpers";
import {
  FundamentalsPageByIsin,
  FundamentalsProvider,
  FundamentalsActions,
} from "../../fundamentals";
import { InvestmentNewsFeedList } from "../../investments/investment/components/InvestmentNewsFeedList";
import { SingleSecurityPageHeader } from "../components/SingleSecurityPageHeader";
import { SecurityMeta } from "../components/SecuritiesAddAndEditForm";
import { useSecurityImages } from "../../../hooks/useSecurityImages";

const tabKeys = {
  fundamentals: "fundamentals",
  news: "news",
};

export interface SingleSecurityPageQueryParams {
  tab?: string;
  id?: string;
  quoteProvider?: string;
}

export const SingleSecurityPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNewsTabVisible = useMandatorVisibility({ hideFor: ["kpmg-germany"] });

  const defaultTabKey = searchParams.get("tab");
  const id = searchParams.get("id");
  const quoteProvider = searchParams.get("quoteProvider");

  const [isAddToWatchlistOpen, setIsAddToWatchlistOpen] = useState(false);
  const { data: watchlistsResponse } = useListAndSearchAllWatchlists();
  const watchlists = (watchlistsResponse?.watchlists ?? []).filter(
    (w) => !!w.id
  ) as WatchlistV2Required[];
  const hasWatchlists = watchlists.length > 0;

  useEffect(() => {
    if (!id || !quoteProvider) {
      navigate("/");
    }
  }, [id, quoteProvider, navigate]);

  const { t } = useTranslation();

  const isMoreXs = useMediaQuery((theme: Theme) => theme.breakpoints.up("sm"));

  const { availableDateRanges, availableCompactDateRanges } =
    useQuoteDateRanges();
  const isComparisonSelectorVisible = useMandatorVisibility({
    hideFor: ["kpmg-germany"],
  });

  const { data } = useGetSecurity(
    {
      id: id!,
      quoteProvider: quoteProvider!,
    },
    {
      enabled: !!id && !!quoteProvider,
    }
  );
  const { data: quoteData } = useGetQuotesV2(
    {
      securityId: [id!],
      quoteProvider: quoteProvider ?? "",
    },
    {
      enabled: !!id && !!quoteProvider,
    }
  );

  const symbol = useMemo(
    () => ({
      ...data?.data,
      ...data?.data?.attributes,
    }),
    [data]
  );

  const breadcrumbs: BreadcrumbItem[] = [
    {
      to: "/",
      text: t("singleSecurityPage.breadcrumbs.home"),
    },
    {
      text: t("singleSecurityPage.breadcrumbs.quotes"),
    },
    {
      text: truncate(symbol?.name),
    },
  ];

  const handleTabChange = (key: string) => {
    if (!id || !quoteProvider) return;

    setSearchParams({
      tab: key,
      id: id,
      quoteProvider: quoteProvider,
    });
  };

  const quote = quoteData?.quotes?.[0];

  const securityForLogos = useMemo(() => {
    if (!symbol || !quoteProvider || !quote?.tickerSymbol) return [];

    return [
      {
        ...symbol,
        type: quoteProvider === "currency" ? "81_fcurr" : "11_stock",
        hasValidIsin: true,
        isin: symbol.isin || symbol.id,
        ticker_symbol: quote.tickerSymbol,
        quoteProvider,
      },
    ];
  }, [symbol, quoteProvider, quote]);

  const logo = useSecurityImages(symbol ? securityForLogos : []);

  return (
    <CardPageLayout
      breadcrumbs={breadcrumbs}
      cardHeading={
        <SingleSecurityPageHeader
          tickerSymbol={quote?.tickerSymbol ?? ""}
          quoteName={symbol?.name ?? ""}
          quoteIsin={symbol?.isin ?? ""}
          image={logo[symbol?.isin || ""] || logo[quote?.tickerSymbol || ""]}
          hasWatchlists={hasWatchlists}
          onAddToWatchlist={() => setIsAddToWatchlistOpen(true)}
        />
      }
      cardChildren={
        symbol ? (
          <ChartControlsProvider
            rangeId="single-security"
            availableDateRanges={availableDateRanges}
            availableCompactDateRanges={availableCompactDateRanges}
            controls={{ showCompare: isComparisonSelectorVisible }}
          >
            <Box>
              <QuoteChartContainer
                quoteTickerSymbol={quote?.tickerSymbol ?? ""}
                quoteProvider={quoteProvider ?? ""}
                quoteType={
                  (symbol?.security_type as InvestmentTypeEnum) ?? "11_stock"
                }
                quoteCurrentValue={quote?.last ?? 0}
                quoteChangePercentAbs={quote?.changePercent ?? 0}
                quoteIsin={symbol?.isin ?? ""}
                quoteCurrency={quote?.currency || "EUR"}
                quoteNotation={quote?.priceUnit}
              />
            </Box>
          </ChartControlsProvider>
        ) : null
      }
    >
      <FundamentalsProvider
        investmentTypeOverride={symbol?.security_type as string}
      >
        <Tabs
          onChange={handleTabChange}
          defaultKey={defaultTabKey}
          buttonElement={
            <FundamentalsActions fundamentalsTabKey={tabKeys.fundamentals} />
          }
        >
          <Tab
            label={
              !isMoreXs ? (
                <AssessmentIcon />
              ) : (
                t("singleSecurityPage.tabs.fundamentals")
              )
            }
            key={tabKeys.fundamentals}
          >
            <FundamentalsActions
              activeTab={tabKeys.fundamentals}
              fundamentalsTabKey={tabKeys.fundamentals}
              inTabsBar={false}
              fullWidth
            />
            {quote?.tickerSymbol && symbol?.isin && quoteProvider && (
              <FundamentalsPageByIsin
                quoteProvider={quoteProvider}
                tickerSymbol={quote?.tickerSymbol}
                isin={symbol.isin}
              />
            )}
          </Tab>
          {symbol?.isin && isNewsTabVisible && (
            <Tab
              label={
                !isMoreXs ? <FiberNewIcon /> : t("singleSecurityPage.tabs.news")
              }
              key={tabKeys.news}
            >
              <InvestmentNewsFeedList isin={symbol.isin} />
            </Tab>
          )}
        </Tabs>
      </FundamentalsProvider>

      <AddToWatchlistModal
        isOpen={isAddToWatchlistOpen}
        onClose={() => setIsAddToWatchlistOpen(false)}
        watchlists={watchlists}
        currentWatchlist={watchlists[0]}
        securityMeta={
          {
            id: symbol?.id ?? "",
            name: symbol?.name ?? "",
            isin: symbol?.isin,
            currency: quote?.currency,
            mappedType: symbol?.security_type as InvestmentTypeEnum,
            quoteProvider: quoteProvider ?? undefined,
          } as SecurityMeta
        }
        onSaved={(savedWatchlistId?: number) => {
          setIsAddToWatchlistOpen(false);
          queryClient.invalidateQueries({
            queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_WATCHLIST_ENTRIES],
          });
          if (savedWatchlistId) {
            navigate(`/watchlists/${savedWatchlistId}`);
          }
        }}
      />
    </CardPageLayout>
  );
};
